import { useMutation } from '@apollo/client';
import {
  InsertFormFieldPositionsDocument,
  UpdateFormFieldPositionsDocument,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import type { FC, ReactElement, ReactNode } from 'react';
import { isValidElement, useCallback, useEffect, useMemo } from 'react';

import { useIsFeatureFlagEnabled } from '@/hooks/useIsFeatureFlagEnabled';
import { evictField } from '@/utils/graphqlUtils';

import useControlledCustomAttributes from '../../custom-attributes/useControlledCustomAttributes';
import { ConditionalFieldsProvider } from '../conditional-fields-provider/ConditionalFieldsProvider';
import { useCustomisableFormDataContext } from '../customisable-form-data/CustomisableFormDataContext';
import { useFormFieldElementsStore } from '../stores/useFormFieldElementsStore';
import { useFormFieldRegistryStore } from '../stores/useFormFieldRegistryStore';
import { useSectionManager } from './hooks/useSectionManager';
import { useTransformSchemaToRequiredFields } from './hooks/useTransformSchemaToRequiredFields';
import { useRiskSmartForm } from './RiskSmartFormContext';
import { SectionedFormLayout } from './SectionedFormLayout';

type Props = {
  children: ReactNode | ReactNode[];
  readOnly?: boolean;
};

/**
 * Wraps a forms fields to support customisation of the order
 * @returns
 */
const CustomisableFieldWrapper: FC<Props> = ({
  children: childrenProp,
  readOnly,
}) => {
  const { parentType, editMode } = useRiskSmartForm();
  const formSectionsEnabled = useIsFeatureFlagEnabled('form_sections');

  if (!parentType) {
    throw new Error(
      'CustomisableForm must have a parentType on the RiskSmartFormProvider'
    );
  }

  const [updateFormFieldPositions] = useMutation(
    UpdateFormFieldPositionsDocument,
    {
      update: (cache) => {
        evictField(cache, 'form_configuration');
        evictField(cache, 'form_field_ordering');
      },
    }
  );
  const [insertFormFieldPositions] = useMutation(
    InsertFormFieldPositionsDocument,
    {
      update: (cache) => {
        evictField(cache, 'form_configuration');
        evictField(cache, 'form_field_ordering');
      },
    }
  );

  const customAttributeElements = useControlledCustomAttributes({
    readOnly,
  });

  const { formFieldOrdering, customAttributeSchema, formSections } =
    useCustomisableFormDataContext();
  const childrenArray = Array.isArray(childrenProp)
    ? childrenProp
    : [childrenProp];
  const children = [...childrenArray, ...customAttributeElements];

  const elementsByKey = (
    children.filter((c) => isValidElement(c)) as ReactElement<
      Record<string, unknown>
    >[]
  ).map((element) => {
    if (!element.key) {
      throw new Error(
        `All children of a CustomisableForm must have a unique 'key' prop, ${
          (element.props as { name?: string }).name ?? 'a child'
        } does not have one.`
      );
    }

    return {
      key: String(element.key),
      value: element,
    };
  });

  // Publish field elements to shared store for preview access (form editor only)
  const setFieldElements = useFormFieldElementsStore((s) => s.setElements);
  const clearFieldElements = useFormFieldElementsStore((s) => s.clear);
  useEffect(() => {
    if (!formSectionsEnabled) {
      return;
    }
    setFieldElements(elementsByKey);

    return () => clearFieldElements();
  }, [
    elementsByKey,
    setFieldElements,
    clearFieldElements,
    formSectionsEnabled,
  ]);

  // Register field IDs in the field registry store (form editor only)
  const registerField = useFormFieldRegistryStore(
    (state) => state.registerField
  );
  const unregisterField = useFormFieldRegistryStore(
    (state) => state.unregisterField
  );

  const customAttributeKeys = useMemo(
    () =>
      new Set(
        (
          customAttributeElements.filter((c) =>
            isValidElement(c)
          ) as ReactElement[]
        ).map((el) => String(el.key))
      ),
    [customAttributeElements]
  );

  useEffect(() => {
    if (!formSectionsEnabled) {
      return;
    }
    const fieldIds = elementsByKey.map((e) => e.key);
    for (const fieldId of fieldIds) {
      registerField(fieldId, {
        fieldId,
        label: fieldId,
        isCustom: customAttributeKeys.has(fieldId),
        defaultRequired: false,
        forcedRequired: false,
      });
    }

    return () => {
      for (const fieldId of fieldIds) {
        unregisterField(fieldId);
      }
    };
  }, [
    elementsByKey,
    registerField,
    unregisterField,
    customAttributeKeys,
    formSectionsEnabled,
  ]);

  useTransformSchemaToRequiredFields(parentType);

  // Use section manager for sectioned layout
  const sectionManager = useSectionManager(
    elementsByKey,
    formSections ?? null,
    formFieldOrdering ?? undefined
  );

  const isUpdate = !!customAttributeSchema;

  const saveFields = useCallback(async () => {
    if (!parentType) {
      return;
    }

    const elementsOrder = sectionManager.sections.flatMap(
      (section) => section.fields
    );

    const mutateFn = isUpdate
      ? updateFormFieldPositions
      : insertFormFieldPositions;

    await mutateFn({
      variables: {
        fieldConfig: elementsOrder.map((key, i) => ({
          FormConfigurationParentType: isUpdate ? parentType : undefined,
          FieldId: key,
          Position: i,
          form: null,
        })),
        fieldIds: elementsOrder,
        parentType,
      },
    });
  }, [
    sectionManager.sections,
    parentType,
    updateFormFieldPositions,
    insertFormFieldPositions,
    isUpdate,
  ]);

  const { setOnSave } = useRiskSmartForm();

  useEffect(() => {
    if (editMode) {
      setOnSave(() => saveFields);
    }

    return () => setOnSave(undefined);
  }, [saveFields, setOnSave, editMode]);

  return (
    <div data-testid={'customisable-form-content'}>
      <SectionedFormLayout
        sections={sectionManager.sections}
        draggedFieldId={sectionManager.draggedFieldId}
        handleFieldReorderInSection={sectionManager.handleFieldReorderInSection}
        setDraggedFieldId={sectionManager.setDraggedFieldId}
        fieldComponents={elementsByKey}
        isEditMode={editMode}
        isReadOnly={readOnly}
        flatLayout={!formSectionsEnabled}
      />
    </div>
  );
};

const CustomisableFieldWrapperWithConditionalProvider: FC<Props> = (props) => (
  <ConditionalFieldsProvider>
    <CustomisableFieldWrapper {...props} />
  </ConditionalFieldsProvider>
);

export default CustomisableFieldWrapperWithConditionalProvider;
